import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, TextField, Button, Grid,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import api from '../../services/api';
import { toastService } from '../../services/toast.service';

const SETTING_KEY = 'mobile_app_version';

interface PlatformPolicy {
  minBuild: number;
  recommendedBuild: number;
  storeUrl: string;
}

interface MobileVersionPolicy {
  ios: PlatformPolicy;
  android: PlatformPolicy;
}

const DEFAULT_POLICY: MobileVersionPolicy = {
  ios: { minBuild: 1, recommendedBuild: 1, storeUrl: '' },
  android: { minBuild: 1, recommendedBuild: 1, storeUrl: '' },
};

const getErrorMessage = (error: any): string => {
  if (error.response?.data) {
    const data = error.response.data;
    if (data.message) {
      return Array.isArray(data.message) ? data.message.join('\n') : data.message;
    }
  }
  return error.message || 'Unknown error';
};

export const AdminMobileVersionSettings: React.FC = () => {
  const [policy, setPolicy] = useState<MobileVersionPolicy>(DEFAULT_POLICY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPolicy();
  }, []);

  const loadPolicy = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/system-settings/${SETTING_KEY}`);
      setPolicy(res.data?.data || DEFAULT_POLICY);
    } catch (error) {
      toastService.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const trimmedPolicy: MobileVersionPolicy = {
        ios: { ...policy.ios, storeUrl: policy.ios.storeUrl.trim() },
        android: { ...policy.android, storeUrl: policy.android.storeUrl.trim() },
      };
      setPolicy(trimmedPolicy);
      await api.post(`/admin/system-settings/${SETTING_KEY}`, { value: trimmedPolicy });
      toastService.success('Версії мобільного застосунку збережено');
    } catch (error) {
      toastService.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const updatePlatform = (platform: 'ios' | 'android', field: keyof PlatformPolicy, value: string) => {
    setPolicy((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: field === 'storeUrl' ? value : Number(value),
      },
    }));
  };

  const renderPlatformFields = (platform: 'ios' | 'android', title: string) => (
    <Grid item xs={12} md={6}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>{title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Мінімальний build"
          type="number"
          value={policy[platform].minBuild}
          onChange={(e) => updatePlatform(platform, 'minBuild', e.target.value)}
        />
        <TextField
          label="Рекомендований build"
          type="number"
          value={policy[platform].recommendedBuild}
          onChange={(e) => updatePlatform(platform, 'recommendedBuild', e.target.value)}
        />
        <TextField
          label="Посилання на стор"
          value={policy[platform].storeUrl}
          onChange={(e) => updatePlatform(platform, 'storeUrl', e.target.value)}
        />
      </Box>
    </Grid>
  );

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SystemUpdateAltIcon color="primary" /> Версії мобільного застосунку
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Мінімальний build примусово блокує застосунок до оновлення (користувач не може закрити вікно).
        Рекомендований — лише пропонує оновитись, можна відкласти.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {renderPlatformFields('ios', 'iOS')}
            {renderPlatformFields('android', 'Android')}
          </Grid>
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Збереження...' : 'Зберегти'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};
